import { throttle, uniqueId } from "lodash";
import { MutableRefObject, useEffect, useRef } from "react";
import { Attachment } from "./Attachment";
import onDomRemoval from "./onDomRemoval";
import calculateContentEditable from "./calculateContentEditable";
import { useAppDispatch } from "../store/hooks";
import {
  attach,
  reflow,
  detach,
  change,
  scroll,
  scrollAreaChange,
} from "../editorsSlice";

interface Props {
  enabled: boolean;
}

export default function useContentEditableAttachment({
  enabled,
}: Props): MutableRefObject<Map<number, Attachment>> {
  const dispatch = useAppDispatch();
  const attachmentsRef = useRef(new Map<number, Attachment>());

  useEffect(() => {
    if (!enabled) {
      console.log("Disabled.. Returning empty!");
      return () => {
        // empty to match signature
      };
    }
    // TODO(api-design): maybe all the observation stuff should be attached in the component using hooks,
    // and this should just handle simple attachment (whether the editor exists). Then the logic could be
    // local to the root component for that editor.
    const attachToContenteditable = (target?: Node) => {
      const attachments = attachmentsRef.current;
      // const editor = document.querySelector("div.ck-editor__main");
      const editor = document.querySelector("div.ql-editor");

      if (!editor) {
        return;
      }

      if (!(editor instanceof HTMLDivElement)) {
        return;
      }

      if (
        Array.from(attachments.values())
          .map((a) => a.editor)
          .includes(editor)
      ) {
        // already attached
        return;
      }
      const attachment = Attachment.tryAttach(
        editor,
        window.document,
        window.location.origin
      );

      if (!attachment) {
        return;
      }

      const id = Number.parseInt(uniqueId(), 10);
      console.log("attaching...");
      attachments.set(id, attachment);
      dispatch(
        attach({
          id,
          ...calculateContentEditable(editor),
          content: editor.outerHTML || "",
          docId: null,
          isLoading: false,
          type: "contenteditable",
        })
      );

      // TODO(optimize): tune the throttle interval?
      const THROTTLE_INTERVAL = 75;
      const updateBoundingClientRect = throttle(() => {
        // converts the DOMRect object into a simple object
        const { top, left, width, height } = editor.getBoundingClientRect();
        dispatch(
          reflow({
            id,
            changes: {
              boundingClientRect: {
                top,
                left,
                width,
                height,
              },
            },
          })
        );
      }, THROTTLE_INTERVAL);

      const resizeObserver = new ResizeObserver(() => {
        dispatch(
          reflow({
            id,
            changes: {
              ...calculateContentEditable(editor),
            },
          })
        );
      });

      onDomRemoval(editor, (observer) => {
        dispatch(detach(id));
        attachments.delete(id);
        observer.disconnect();
        attachToContenteditable(
          document.querySelector("div.ck-editor__main") as Node
        );
      });

      const doSetText = () => {
        dispatch(change({ id, content: editor.outerHTML }));
      };
      const doSetScroll = () => {
        const { scrollLeft, scrollTop } = editor;
        dispatch(scroll({ editorId: id, scrollLeft, scrollTop }));
      };

      // Poll
      const POLL_INTERVAL_MS = 1000;

      const pollBoundingClientRect = () => {
        updateBoundingClientRect();
        if (!editor.isConnected) {
          console.log("pollBoundingClientRect");
          dispatch(detach(id));
          attachments.delete(id);
          attachToContenteditable(
            document.querySelector("div.ck-editor__main") as Node
          );
        } else {
          setTimeout(pollBoundingClientRect, POLL_INTERVAL_MS);
        }
      };
      setTimeout(pollBoundingClientRect, POLL_INTERVAL_MS);

      window.addEventListener("resize", updateBoundingClientRect);
      resizeObserver.observe(editor);
      editor.addEventListener("focus", doSetText);
      editor.addEventListener("change", () => {
        dispatch(
          scrollAreaChange({
            id,
            scrollHeight: editor.scrollHeight,
            scrollWidth: editor.scrollWidth,
          })
        );
        doSetText();
      });
      editor.addEventListener("keyup", doSetText);
      // editor.addEventListener('paste', updateBoundingClientRect);
      editor.addEventListener("scroll", doSetScroll);
      document.addEventListener("scroll", updateBoundingClientRect);
      editor.spellcheck = false;
    };

    attachToContenteditable(
      document.querySelector("div.ck-editor__main") as Node
    );

    // TODO: what about focusing?
    const clickListener = (event: Event) => {
      const { target } = event;
      attachToContenteditable(target as Node);
    };

    // On a selection change, check if the selection is within an editor
    const selectionListener = () => {
      const selection = document.getSelection();
      if (selection && selection.rangeCount > 0) {
        const range = selection.getRangeAt(0);
        attachToContenteditable(range.startContainer);
        attachToContenteditable(range.endContainer);
      }
    };

    document.addEventListener("click", clickListener);
    document.addEventListener("selectionchange", selectionListener);
    return () => {
      document.removeEventListener("click", clickListener);
      document.removeEventListener("selectionchange", selectionListener);
    };
  }, [dispatch, enabled]);

  return attachmentsRef;
}
